import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';

const paymentMethods = [
  {
    name: 'Mastercard',
    type: 'Card',
    logo: '/payment-logos/mastercard.svg'
  },
  {
    name: 'UPI',
    type: 'Instant transfer',
    logo: '/payment-logos/upi.svg'
  },
  {
    name: 'Google Pay',
    type: 'UPI app',
    logo: '/payment-logos/gpay.svg'
  },
  {
    name: 'PhonePe',
    type: 'UPI app',
    logo: '/payment-logos/phonepe.svg'
  }
];

const INDIA_STATES = [
  'Andhra Pradesh',
  'Arunachal Pradesh',
  'Assam',
  'Bihar',
  'Chhattisgarh',
  'Goa',
  'Gujarat',
  'Haryana',
  'Himachal Pradesh',
  'Jharkhand',
  'Karnataka',
  'Kerala',
  'Madhya Pradesh',
  'Maharashtra',
  'Manipur',
  'Meghalaya',
  'Mizoram',
  'Nagaland',
  'Odisha',
  'Punjab',
  'Rajasthan',
  'Sikkim',
  'Tamil Nadu',
  'Telangana',
  'Tripura',
  'Uttar Pradesh',
  'Uttarakhand',
  'West Bengal',
  'Delhi',
  'Chandigarh',
  'Jammu and Kashmir',
  'Ladakh',
  'Puducherry'
];

export default function CheckoutPage() {
  const { items, subtotal, totalItems } = useCart();

  const [formData, setFormData] = useState({
    email: '',
    emailOffers: false,
    firstName: '',
    lastName: '',
    address: '',
    apartment: '',
    city: '',
    area: '',
    state: 'Haryana',
    pinCode: '',
    phone: '',
    smsOffers: false,
    orderNote: ''
  });

  const [pincodeInfo, setPincodeInfo] = useState({
    loading: false,
    error: '',
    areas: []
  });

  const [shippingMethod, setShippingMethod] = useState('free');
  const [paymentNotice, setPaymentNotice] = useState('');

  const shippingPrice = shippingMethod === 'express' ? 99 : 0;
  const totalWithShipping = subtotal + shippingPrice;

  const addressReady = useMemo(() => {
    return (
      formData.address.trim() &&
      formData.city.trim() &&
      formData.state.trim() &&
      formData.pinCode.trim().length === 6 &&
      formData.phone.trim().length >= 10
    );
  }, [formData.address, formData.city, formData.state, formData.pinCode, formData.phone]);

  const shippingLabel = useMemo(() => {
    if (!addressReady) {
      return 'Enter your shipping address to view available shipping methods.';
    }
    return 'Select a shipping method below.';
  }, [addressReady]);

  useEffect(() => {
    const pin = formData.pinCode;

    if (pin.length !== 6) {
      setPincodeInfo({ loading: false, error: '', areas: [] });
      return;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      setPincodeInfo({ loading: true, error: '', areas: [] });

      try {
        const response = await fetch(`https://api.postalpincode.in/pincode/${pin}`);
        const data = await response.json();
        const entry = Array.isArray(data) ? data[0] : null;

        if (!entry || entry.Status !== 'Success' || !Array.isArray(entry.PostOffice)) {
          if (!cancelled) {
            setPincodeInfo({ loading: false, error: 'Invalid PIN code. Please check again.', areas: [] });
          }
          return;
        }

        const postOffices = entry.PostOffice;
        const areaNames = Array.from(
          new Set(
            postOffices
              .map((office) => String(office.Name || '').trim())
              .filter(Boolean)
          )
        );

        const first = postOffices[0];

        if (!cancelled) {
          setFormData((prev) => ({
            ...prev,
            city: first?.District || prev.city,
            state: first?.State || prev.state,
            area: prev.area && areaNames.includes(prev.area) ? prev.area : areaNames[0] || prev.area
          }));

          setPincodeInfo({
            loading: false,
            error: '',
            areas: areaNames
          });
        }
      } catch (_error) {
        if (!cancelled) {
          setPincodeInfo({
            loading: false,
            error: 'Could not fetch city details right now. You can still fill manually.',
            areas: []
          });
        }
      }
    }, 450);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [formData.pinCode]);

  const onChange = (event) => {
    const { name, value, type, checked } = event.target;

    let nextValue = type === 'checkbox' ? checked : value;

    if (name === 'pinCode') {
      nextValue = String(value || '').replace(/\D/g, '').slice(0, 6);
    }

    if (name === 'phone') {
      nextValue = String(value || '').replace(/\D/g, '').slice(0, 10);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: nextValue
    }));
  };

  const onPayClick = () => {
    setPaymentNotice('Thank you for showing interest, coming soon.');
  };

  if (!items.length) {
    return (
      <section className="checkout-page">
        <article className="empty-cart">
          <h2>No items to checkout</h2>
          <p>Your cart is empty. Add product first, then come to checkout.</p>
          <Link to="/product" className="solid-btn">
            Go to Product
          </Link>
        </article>
      </section>
    );
  }

  return (
    <section className="checkout-page">
      <div className="checkout-layout">
        <div className="checkout-main">
          <div className="checkout-head">
            <h1>Checkout</h1>
            <p>
              Contact
              <span> • </span>
              <Link to="/login">Sign in</Link>
            </p>
          </div>

          <article className="checkout-card">
            <h2>Email</h2>
            <input
              name="email"
              type="email"
              value={formData.email}
              onChange={onChange}
              placeholder="Email"
              required
            />
            <label className="check-row">
              <input
                name="emailOffers"
                type="checkbox"
                checked={formData.emailOffers}
                onChange={onChange}
              />
              <span>Email me with news and offers</span>
            </label>
          </article>

          <article className="checkout-card">
            <h2>Delivery</h2>
            <div className="checkout-grid two">
              <div>
                <label>Country</label>
                <input value="India" readOnly />
              </div>
              <div>
                <label>State</label>
                <select name="state" value={formData.state} onChange={onChange}>
                  {INDIA_STATES.map((state) => (
                    <option value={state} key={state}>
                      {state}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="checkout-grid two">
              <div>
                <label>First name (optional)</label>
                <input name="firstName" value={formData.firstName} onChange={onChange} />
              </div>
              <div>
                <label>Last name</label>
                <input name="lastName" value={formData.lastName} onChange={onChange} />
              </div>
            </div>

            <div className="checkout-grid one">
              <div>
                <label>Address</label>
                <input name="address" value={formData.address} onChange={onChange} required />
              </div>
            </div>

            <div className="checkout-grid one">
              <div>
                <label>Apartment, suite, etc. (optional)</label>
                <input name="apartment" value={formData.apartment} onChange={onChange} />
              </div>
            </div>

            <div className="checkout-grid three">
              <div>
                <label>City</label>
                <input
                  name="city"
                  value={formData.city}
                  onChange={onChange}
                  placeholder="Auto-filled from PIN"
                  required
                />
              </div>
              <div>
                <label>PIN code</label>
                <input name="pinCode" value={formData.pinCode} onChange={onChange} required />
              </div>
              <div>
                <label>Phone</label>
                <input name="phone" value={formData.phone} onChange={onChange} required />
              </div>
            </div>

            {formData.pinCode.length > 0 ? (
              <p
                className={`pincode-status ${
                  pincodeInfo.loading
                    ? 'loading'
                    : pincodeInfo.error
                      ? 'error'
                      : pincodeInfo.areas.length
                        ? 'success'
                        : ''
                }`}
              >
                {pincodeInfo.loading
                  ? 'Fetching city and area from PIN...'
                  : pincodeInfo.error
                    ? pincodeInfo.error
                    : pincodeInfo.areas.length
                      ? `PIN matched. ${pincodeInfo.areas.length} area(s) found.`
                      : ''}
              </p>
            ) : null}

            {pincodeInfo.areas.length > 0 ? (
              <div className="checkout-grid one">
                <div>
                  <label>Area / Locality</label>
                  <select name="area" value={formData.area} onChange={onChange}>
                    {pincodeInfo.areas.map((area) => (
                      <option value={area} key={area}>
                        {area}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            ) : null}

            <label className="check-row">
              <input
                name="smsOffers"
                type="checkbox"
                checked={formData.smsOffers}
                onChange={onChange}
              />
              <span>Text me with news and offers</span>
            </label>

            <div className="checkout-grid one">
              <div>
                <label>Order note (optional)</label>
                <textarea
                  name="orderNote"
                  value={formData.orderNote}
                  onChange={onChange}
                  placeholder="Delivery timing, landmark, or any request"
                  rows={3}
                />
              </div>
            </div>
          </article>

          <article className="checkout-card">
            <h2>Shipping method</h2>
            <p className={`shipping-note ${addressReady ? 'ready' : ''}`}>{shippingLabel}</p>

            {addressReady ? (
              <div className="shipping-options">
                <label className={`shipping-option ${shippingMethod === 'free' ? 'active' : ''}`}>
                  <input
                    type="radio"
                    name="shippingMethod"
                    value="free"
                    checked={shippingMethod === 'free'}
                    onChange={() => setShippingMethod('free')}
                  />
                  <div>
                    <strong>Standard Free Shipping</strong>
                    <p>Delivery in 20-25 days</p>
                  </div>
                  <span>Free</span>
                </label>

                <label className={`shipping-option ${shippingMethod === 'express' ? 'active' : ''}`}>
                  <input
                    type="radio"
                    name="shippingMethod"
                    value="express"
                    checked={shippingMethod === 'express'}
                    onChange={() => setShippingMethod('express')}
                  />
                  <div>
                    <strong>Priority Dispatch</strong>
                    <p>Faster processing and support lane</p>
                  </div>
                  <span>₹99</span>
                </label>
              </div>
            ) : null}
          </article>

          <article className="checkout-card">
            <h2>Payment methods</h2>
            <p className="payment-caption">Card + UPI options available</p>

            <div className="payment-grid">
              {paymentMethods.map((method) => (
                <div className="payment-method" key={method.name}>
                  <div className="payment-logo-wrap">
                    <img src={method.logo} alt={`${method.name} logo`} loading="lazy" />
                  </div>
                  <div>
                    <strong>{method.name}</strong>
                    <p>{method.type}</p>
                  </div>
                </div>
              ))}
            </div>

            <button type="button" className="solid-btn payment-btn" onClick={onPayClick}>
              Complete Payment
            </button>
            {paymentNotice ? <p className="payment-soon-msg">{paymentNotice}</p> : null}
          </article>
        </div>

        <aside className="checkout-summary">
          <h2>Order Summary</h2>
          <div className="checkout-items">
            {items.map((item) => (
              <article key={item.id} className="checkout-item">
                <img src={item.image} alt={item.name} />
                <div>
                  <h3>{item.name}</h3>
                  <p>{item.colorName}</p>
                  <p>Qty: {item.quantity}</p>
                </div>
                <strong>₹ {(item.price * item.quantity).toLocaleString('en-IN')}</strong>
              </article>
            ))}
          </div>

          <div className="summary-row">
            <span>Items ({totalItems})</span>
            <strong>₹ {subtotal.toLocaleString('en-IN')}.00</strong>
          </div>
          <div className="summary-row">
            <span>Shipping</span>
            <strong>{shippingPrice === 0 ? 'Free' : `₹ ${shippingPrice}`}</strong>
          </div>
          <div className="summary-row total">
            <span>Total</span>
            <strong>₹ {totalWithShipping.toLocaleString('en-IN')}.00</strong>
          </div>
        </aside>
      </div>
    </section>
  );
}
